import { useContext } from "react";

import {
  FormCategory,
  FormType,
  ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE,
  getDonationOrTicketingFormLink,
  PaymentMethod,
} from "@simplyk/common";
import { useStripe } from "@stripe/react-stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { UseFormSetValue } from "react-hook-form";

import { getCashAppConfirmationFailure, PaymentErrorReport, unknownPaymentErrorCode } from "./helper";
import { HandlePostSubmit } from "./useStripePayment";

import { useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import {
  clearCashAppPaymentStarted,
  markCashAppPaymentStarted,
} from "@/canvas/features/PostFormSubmit/helpers/cashAppPaymentStarted";
import { buildPostFormSubmitUrl } from "@/canvas/features/PostFormSubmit/helpers/postFormSubmitUrl";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getBillingDetails } from "@/common/helpers/payment";
import { PaymentMode } from "@/common/helpers/stripe";
import { AppRouterType } from "@/common/helpers/trpc";
import { useRegistrationRedirect } from "@/common/hooks/useRegistrationRedirect";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { FormOrganizationOutput } from "@/common/types/trpc";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export type GetThankYouUrl = ({
  command,
  paymentInput,
  formType,
  organization,
  emailUpdateToken,
  buyerTicketToken,
}: {
  command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
  paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
  formType: FormType;
  organization: FormOrganizationOutput;
  emailUpdateToken?: string;
  buyerTicketToken?: string;
}) => string;

export const useCashapp = ({
  setValue,
  handlePaymentError,
  getThankYouUrl,
  handlePostSubmit,
}: {
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  handlePaymentError: (report: PaymentErrorReport) => void;
  getThankYouUrl: GetThankYouUrl;
  handlePostSubmit: HandlePostSubmit;
}) => {
  const { handleSubmitCommand } = useSubmitCommand({ setValue });
  const { t } = useTranslate();
  const { organization, commandId, formType, category, generateETicket, formData } = useContext(FrontendFormContext);
  const { hasADate } = useTicketingFormV2ContextOrDefaults();
  const { shouldRedirectToPeerToPeer, getRegistrationRedirectUrl } = useRegistrationRedirect();
  const stripe = useStripe();
  const { isoLocale } = useLocaleContext();

  const payByCashapp = async (
    submitCommandInput: SubmitCommandFormInput,
    paymentMode: PaymentMode
  ): Promise<string | undefined> => {
    const express = Boolean(submitCommandInput.isExpressCheckout);

    if (!stripe) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.cashApp1.code }),
        step: "stripe-load",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    const submitResponse = await handleSubmitCommand({
      ...submitCommandInput,
      paymentMethod: PaymentMethod.CashApp,
    });

    if (submitResponse && isPaymentFailed(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.error.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.error.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error.message,
        step: "server-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const clientSecret = submitResponse.data?.clientSecret;
    const command = submitResponse.data?.command;
    const emailUpdateToken = submitResponse.data?.emailUpdateToken;
    const buyerTicketToken = submitResponse.data?.buyerTicketToken;
    if (!clientSecret || !command) {
      handlePaymentError({
        rawErrorCode: !clientSecret ? "missing_client_secret" : "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.cashApp1.code }),
        step: "server-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    const registrationRedirectUrl = shouldRedirectToPeerToPeer(submitCommandInput.paymentInput, formType)
      ? getRegistrationRedirectUrl({
          firstName: command.firstName,
          lastName: command.lastName,
          email: command.email,
          commandId: command.id,
        })
      : null;
    const thankYouUrl =
      registrationRedirectUrl ??
      getThankYouUrl({
        command,
        paymentInput: submitCommandInput.paymentInput,
        formType,
        organization,
        emailUpdateToken,
        buyerTicketToken,
      });

    const formUrl = getDonationOrTicketingFormLink({
      form: {
        id: formData.id,
        path: formData.path,
        formType,
        formCategory: category as FormCategory,
        isPrimaryCampaign: undefined,
        isPrimaryTeam: undefined,
      },
      isoLocale,
    });
    const returnUrl = buildPostFormSubmitUrl({
      commandId,
      formId: formData.id,
      formType: submitCommandInput.formType,
      formCategory: category,
      isoLocale,
      thankYouUrl: thankYouUrl || "",
      formUrl,
      organizationName: organization.name,
      hasADate,
      generateETicket: generateETicket && command.productsAmount > 0,
      generateTaxReceipt: (command.eligibleAmount || 0) > 0 && organization.canGenerateReceipt,
      organizationId: organization.id,
      transactionId: submitResponse.data?.transaction?.id,
      productsAmount: command.productsAmount,
      extraDonation: command.extraDonation,
      currency: command.currency,
    });
    const confirmationOptions = {
      return_url: returnUrl,
      payment_method: {
        billing_details: getBillingDetails(submitCommandInput.paymentInput, organization),
      },
    };
    markCashAppPaymentStarted(returnUrl);
    const confirmationResult =
      paymentMode === "setup"
        ? await stripe.confirmCashappSetup(clientSecret, confirmationOptions)
        : await stripe.confirmCashappPayment(clientSecret, confirmationOptions);
    clearCashAppPaymentStarted(returnUrl);
    const confirmationFailure = getCashAppConfirmationFailure(confirmationResult);

    if (confirmationFailure) {
      handlePaymentError({
        rawErrorCode: confirmationFailure.code,
        rawErrorMessage: confirmationFailure.message || t("common", "payment.cashApp.notCompleted.title"),
        step: "manual-confirmation",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const result = await handlePostSubmit({
      paymentInput: submitCommandInput.paymentInput,
      command,
      transaction: submitResponse.data?.transaction ?? null,
      formType,
      isExpressCheckout: express,
      emailUpdateToken,
      buyerTicketToken,
    });
    if (result.error || !result.returnUrl) {
      handlePaymentError({
        rawErrorCode: result.error ? "post_submit_error" : "missing_return_url",
        rawErrorMessage:
          result.error ||
          t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.cashApp2.code }),
        step: "post-submit",
        paymentMethod: PaymentMethod.CashApp,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
      return;
    }
    return result.returnUrl;
  };

  return { payByCashapp };
};
